// Global Instructions Rule Applied!

/**Centralized globals and enums used across the state manager layer*/

// ========================
// Left menu & tab keys (must match menu_items.key / tab module in Supabase and tabSlice)
// ========================
export const MENU_ITEM_KEY_FUNDRAISING_PIPELINE = 'fundraising-pipeline'
export const MENU_ITEM_KEY_DOWNLOADS = 'downloads'
export const MENU_ITEM_KEY_REPORTS = 'reports'
/** Table list pages — must match `menu_items.key` / tabSlice `module`. */
export const MENU_ITEM_KEY_DEALS = 'deals'
export const MENU_ITEM_KEY_CONTACTS = 'contacts'
export const MENU_ITEM_KEY_COMPANIES = 'companies'
export const MENU_ITEM_KEY_FUNDRAISING = 'fundraising'
export const MENU_ITEM_KEY_FUNDS = 'funds'
export const MENU_ITEM_KEY_INVESTMENTS = 'investments'
export const MENU_ITEM_KEY_FUND_TRANSACTIONS = 'fund-transactions'
export const MENU_ITEM_KEY_CAPITAL_CALLS = 'capital-calls'
export const MENU_ITEM_KEY_CONSOLIDATED_FUNDS = 'consolidated-funds'
export const MENU_ITEM_KEY_FEE_BOOKING = 'fee-booking'
export const MENU_ITEM_KEY_INTEREST_ACCRUALS = 'interest-accruals'
export const MENU_ITEM_KEY_CAPITAL_CALL_POSITION = 'capital-call-position'
export const MENU_ITEM_KEY_JOURNAL_ENTRIES = 'journal-entries'
export const MENU_ITEM_KEY_INVESTOR_STATEMENTS = 'investor-statements'
export const MENU_ITEM_KEY_ASSET_TRANSACTIONS = 'asset-transactions'
export const MENU_ITEM_KEY_LP_ALLOCATION = 'lp-allocation'
export const MENU_ITEM_KEY_LPS = 'lps'
export const MENU_ITEM_KEY_TRANCHES = 'tranches'

// ========================
// Reporting / downloads (must match `reporting_configs` / `cfg.reporting_*` and report-type APIs)
// ========================
/** Legacy report-type key for APIs / persisted prefs migration (prefer `reporting_config.id` in UI). */
export const TABLE_KEY_SCHEDULE_OF_INVESTMENTS = 'schedule_of_investments'

/** `cfg.reporting_config.name` for the Schedule of Investments report (SOI data + table). */
export const REPORT_CONFIG_NAME_SCHEDULE_OF_INVESTMENTS = 'Schedule of Investments'

/** Legacy report-type key for APIs / persisted prefs migration (prefer `reporting_config.id` in UI). */
export const TABLE_KEY_CASH_FLOWS = 'cash_flows'

/** `cfg.reporting_config.name` for the Cash Flows report (cash-flow ledger data + table). */
export const REPORT_CONFIG_NAME_CASH_FLOWS = 'Cash Flows'

/** `cfg.reporting_config.name` for the Deals pipeline / grid table column config. */
export const REPORT_CONFIG_NAME_DEALS = 'Deals'

/** `cfg.reporting_config.name` for the Companies / accounts grid column config. */
export const REPORT_CONFIG_NAME_COMPANIES = 'Companies'

/** `cfg.reporting_config.name` for the Contacts grid column config. */
export const REPORT_CONFIG_NAME_CONTACTS = 'Contacts'

/** `cfg.reporting_config.name` for the Fundraising / prospects grid column config. */
export const REPORT_CONFIG_NAME_PROSPECTS = 'Prospects'
export const REPORT_CONFIG_NAME_FUNDS = 'Funds'

/** `cfg.reporting_config.name` for the Fund Transactions grid column config. */
export const REPORT_CONFIG_NAME_FUND_TRANSACTIONS = 'Fund Transactions'

/** `cfg.reporting_config.name` for the Capital Calls grid column config. */
export const REPORT_CONFIG_NAME_CAPITAL_CALLS = 'Capital Calls'
export const REPORT_CONFIG_NAME_JOURNAL_ENTRIES = 'Journal Entries'
/** `cfg.reporting_config.name` for the Investor Statements runs grid. */
export const REPORT_CONFIG_NAME_INVESTOR_STATEMENT_RUNS = 'Investor Statement Runs'
export const REPORT_CONFIG_NAME_ASSET_TRANSACTIONS = 'Asset Transactions'
export const REPORT_CONFIG_NAME_LP_ALLOCATION = 'LP Allocation'
export const REPORT_CONFIG_NAME_LPS = 'LPs'
export const REPORT_CONFIG_NAME_TRANCHES = 'Tranches'

/** Default footer link when a menu section has no resolvable first item (align with deal pipeline home). */
export const LEFT_MENU_FOOTER_FALLBACK_HREF = '/deals/pipeline'

// ========================
// General
// ========================
export const FIELD_KEY_SEPARATOR = '__'
export const FIELD_KEY_PROG = `prog${FIELD_KEY_SEPARATOR}` as const
export const FIELD_KEY_LABEL = `${FIELD_KEY_SEPARATOR}label` as const
export const FIELD_KEY_ACTION = `${FIELD_KEY_SEPARATOR}action` as const
export const TABLE_PRIMARY_KEY = 'id'

// ========================
// Write Transaction Action Types
// ========================
export const ACTION_TYPE_INSERT = 'insert'
export const ACTION_TYPE_UPSERT = 'upsert'
export const ACTION_TYPE_UPDATE = 'update'
export const ACTION_TYPE_DELETE = 'delete'
export const ACTION_TYPE_RESIGN = 'resign'

// ========================
// Table Names
// ========================
export const TABLE_DEALS = 'deals'
export const TABLE_ACCOUNTS = 'accounts'
export const TABLE_ROUNDS = 'rounds'
export const TABLE_PIPELINE_STAGES = 'pipeline_stages'
export const TABLE_PIPELINE_DEALS = 'pipeline_deals'
export const TABLE_PIPELINE_CONFIG = 'pipeline_config'
export const TABLE_TASKS = 'tasks'
export const TABLE_TASK_DEALS = 'task_deals'
export const TABLE_TASK_ACCOUNTS = 'task_accounts'
export const TABLE_TASK_ASSIGNEES = 'task_assignees'
export const TABLE_CONTACTS = 'contacts'
export const TABLE_CONTACT_ACCOUNTS = 'contact_accounts'
export const TABLE_CONTACT_DEALS = 'contact_deals'
export const TABLE_RELATIONSHIP_TYPES = 'relationship_types'
export const TABLE_DEAL_COMMITMENTS = 'deal_commitments'
export const TABLE_INTERACTIONS = 'interactions'
export const TABLE_INTERACTION_ACCOUNTS = 'interaction_accounts'
export const TABLE_INTERACTION_DEALS = 'interaction_deals'
export const TABLE_INTERACTION_PARTICIPANTS = 'interaction_participants'
export const TABLE_ACCOUNT_ROLES = 'account_roles'
export const TABLE_PROSPECTS = 'prospects'
export const TABLE_PIPELINE_PROSPECTS = 'pipeline_prospects'
export const TABLE_CONTACT_PROSPECTS = 'contact_prospects'
export const TABLE_TASK_PROSPECTS = 'task_prospects'
export const TABLE_INTERACTION_PROSPECTS = 'interaction_prospects'
export const TABLE_FUNDS = 'funds'
// Investor entities (public.investors) — a form-only module: opened from the
// investor links of the Investors grids and the account's Funds tab.
export const TABLE_INVESTORS = 'investors'
// Commitments — form-only modules. 'commitments' is one fund_commitments row
// (a standalone commitment or a pocket of a consolidated one) and the New
// commitment form; 'consolidated-commitments' is one fund_group_commitments
// row. Opened from the Investors grids, the Investor form and the account's
// Funds tab.
export const TABLE_COMMITMENTS = 'commitments'
export const TABLE_CONSOLIDATED_COMMITMENTS = 'consolidated-commitments'
export const TABLE_FUND_TRANSACTIONS = 'fund-transactions'
export const TABLE_CAPITAL_CALLS = 'capital-calls'
// Distributions (docs/WATERFALL_CARRY_DESIGN.md phase 3): the full form only -
// reached from the fund's Distributions tab, the Fund Transactions hub's New
// menu and a Booked realization; there is no distributions grid yet.
export const TABLE_DISTRIBUTIONS = 'distributions'
// Consolidated funds (fund groups) — virtual umbrellas over member funds.
export const TABLE_FUND_GROUPS = 'consolidated-funds'
export const TABLE_JOURNAL_ENTRIES = 'journal-entries'
export const TABLE_INVESTOR_STATEMENTS = 'investor-statements'
export const TABLE_ASSET_TRANSACTIONS = 'asset-transactions'
// Tranche / security full forms (opened by record links: the Interest
// Accruals workbench, the tranche and security forms).
export const TABLE_TRANCHES = 'tranches'
export const TABLE_SECURITIES = 'securities'
export const TABLE_LP_ALLOCATION = 'lp-allocation'
export const TABLE_LPS = 'lps'
export const TABLE_FILE_URLS_DEALS = 'file_urls_deals'

// Company documents — human uploads AND the PDF attachments the internal
// agent ingests from monitored mailboxes (internal agents §D2). The generic
// files controller derives the Storage folder from this table name, so the
// bytes land under accounts/{account_id}/.
export const TABLE_FILE_URLS_ACCOUNTS = 'file_urls_accounts'

// Email module — shared (per-campaign) attachments. Per-recipient
// attachments arrive in Phase 6 with a separate file_urls_email_recipients
// table; nothing in Phase 2 uses it yet.
export const TABLE_FILE_URLS_EMAIL_CAMPAIGNS = 'file_urls_email_campaigns'
export const FIELD_EMAIL_CAMPAIGN_ID = 'email_campaign_id'

// Email design templates — template-level attachments, copied onto a
// campaign when the template is applied in the compose form.
export const TABLE_FILE_URLS_EMAIL_DESIGN_TEMPLATES = 'file_urls_email_design_templates'
export const FIELD_EMAIL_DESIGN_TEMPLATE_ID = 'email_design_template_id'

// ========================
// Pipeline Type UUIDs
// ========================
export const PIPELINE_TYPE_DEAL_ORIGINATION = '9c8d988c-02ac-4303-a87f-200912fe87ab'
export const PIPELINE_TYPE_FUNDRAISING = '6b917aba-3aa5-4290-9203-baf42c619c66'
export const PIPELINE_TYPE_DEAL_EXECUTION = '44884f55-31e9-4d51-96e7-163d14c3233a'

// The three ids above ARE the config keys since 20261111120100: pipeline types
// moved to cfg.pipeline_types with their lookup ids preserved, and every config
// column that named a type by its renameable label now holds ids. The
// PIPELINE_TYPE_LABEL_* constant that stood here is gone with them.

// ========================
// Pipeline View Types
// ========================
export const VIEW_TYPE_KANBAN_BOARD = 'kb'
export const VIEW_TYPE_TABLE = 'tbl'

// ========================
// Relationship Category UUIDs
// ========================
export const RELATIONSHIP_CATEGORY_INTRODUCED_BY = '76c9ff9d-cd72-422b-b1f1-e9350c535c65'
export const RELATIONSHIP_CATEGORY_SOURCED_BY = '0753a1d8-dbbc-47cf-b2db-d3a189080096'
export const RELATIONSHIP_CATEGORY_LEAD = '92fcac03-52e3-45cb-8d74-29b895952ef9'
export const RELATIONSHIP_CATEGORY_FOUNDER = '74135229-e63e-4a3c-a682-7d9fbb44cabb'

// ========================
// Relationship Type UUIDs
// ========================
export const RELATIONSHIP_TYPE_FOUNDER = '0fe0dcfe-73b0-408f-afe1-5d32474fa6b3'
export const RELATIONSHIP_TYPE_EMPLOYEE = 'fa6de641-1547-4839-af4d-9ee35d431448'
export const RELATIONSHIP_TYPE_ADVISOR = 'eaaecebb-22a8-4eab-ad0e-7d43e9ac6a2c'
export const RELATIONSHIP_TYPE_OTHER = 'a69be2f3-d371-4b14-95dd-6693bfd8b60d'

// ========================
// Currency UUIDs
// ========================
export const CURRENCY_USD = 'e940a0cd-ed07-ec11-b6e5-000d3a9aa865'

// ========================
// Pipeline Stage UUIDs
// ========================
export const DEFAULT_PIPELINE_STAGE_ID = '2fe1f0de-90e2-48e7-8faf-8f7ca194f5e4'

// ========================
// Account Role UUIDs
// ========================
export const PORTFOLIO_COMPANY_ROLE_ID = '64cbba83-8c3d-44ae-9267-1a9b6e4f9c55'

// ========================
// Account Role Choice UUIDs
// ========================
export const ACCOUNT_ROLE_CHOICE_PIPELINE_COMPANY = '1f501272-2fd6-4171-8518-06b4ebe7d8e4'
export const ACCOUNT_ROLE_CHOICE_PORTFOLIO_COMPANY = '64cbba83-8c3d-44ae-9267-1a9b6e4f9c55'
export const ACCOUNT_ROLE_CHOICE_DEAD = 'bff0b378-f0a3-46a6-9448-41355cd2f456'
export const ACCOUNT_ROLE_CHOICE_EXITED = 'c51961ea-3112-4881-8f3e-ad77f46259c0'
export const ACCOUNT_ROLE_CHOICE_LP = '282c3feb-3697-4b82-bc11-62db467d86a3'
export const ACCOUNT_ROLE_CHOICE_CO_INVESTOR = '16a8a2be-1485-4483-af1d-c060d653dcd0'
export const ACCOUNT_ROLE_CHOICE_PASSED = '72c96f20-9a5c-407f-8ea4-fae26bb5f37a'
export const ACCOUNT_ROLE_CHOICE_PASSED_TRACK = '04964593-28f4-4e66-a019-4292fc4ac3d4'
export const ACCOUNT_ROLE_CHOICE_LOST = 'ff3cdeae-f2d6-407d-b29d-f04d73f3c641'

// ========================
// File URLs Column Names
// ========================
export const FIELD_DEAL_ID = 'deal_id'
export const FIELD_ACCOUNT_ID = 'account_id'

// ========================
// Validation Patterns
// ========================
// UUID regex that accepts both RFC 4122 compliant UUIDs and Microsoft GUIDs
export const UUID_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
